import React, { useState, useEffect, useCallback } from "react";
import api from "../../../api/axios";
import toast from "react-hot-toast";
import { Plus, Trash2, Clock, CalendarOff, Loader2 } from "lucide-react";

const DAYS = ["Pazar", "Pazartesi", "Salı", "Çarşamba", "Perşembe", "Cuma", "Cumartesi"];
const fmtTime = (t) => (t ? t.slice(0, 5) : "");
const fmtDate = (ymd) => {
  if (!ymd) return "";
  const [y, m, d] = ymd.split("-");
  return `${d}.${m}.${y}`;
};

export default function AvailabilityPage() {
  const [employees, setEmployees] = useState([]);
  const [employeeId, setEmployeeId] = useState("");
  const [rules, setRules] = useState([]);
  const [exceptions, setExceptions] = useState([]);
  const [loading, setLoading] = useState(false);

  const [newRule, setNewRule] = useState({ dayOfWeek: 1, startTime: "09:00", endTime: "18:00", slotDurationMinutes: 30 });
  const [newException, setNewException] = useState({ date: "", reason: "" });
  const [savingRule, setSavingRule] = useState(false);
  const [savingExc, setSavingExc] = useState(false);

  useEffect(() => {
    api.get("/employee")
      .then((res) => {
        const list = res.data || [];
        setEmployees(list);
        if (list.length > 0) setEmployeeId(String(list[0].employeeId));
      })
      .catch(() => toast.error("Personel listesi yüklenemedi"));
  }, []);

  const loadData = useCallback(async (empId) => {
    if (!empId) return;
    setLoading(true);
    try {
      const [rulesRes, excRes] = await Promise.all([
        api.get("/availability", { params: { employeeId: empId } }),
        api.get("/availability/exceptions", { params: { employeeId: empId } }),
      ]);
      setRules(rulesRes.data || []);
      setExceptions(excRes.data || []);
    } catch {
      toast.error("Uygunluk verileri yüklenemedi");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (employeeId) loadData(employeeId);
  }, [employeeId, loadData]);

  const addRule = async (e) => {
    e.preventDefault();
    if (newRule.endTime <= newRule.startTime) return toast.error("Bitiş saati başlangıçtan sonra olmalı");
    setSavingRule(true);
    try {
      await api.post("/availability", {
        employeeId: Number(employeeId),
        dayOfWeek: Number(newRule.dayOfWeek),
        startTime: newRule.startTime,
        endTime: newRule.endTime,
        slotDurationMinutes: Number(newRule.slotDurationMinutes) || 30,
        isActive: true,
      });
      toast.success("Uygunluk kuralı eklendi");
      loadData(employeeId);
    } catch {
      toast.error("Kural eklenemedi");
    } finally {
      setSavingRule(false);
    }
  };

  const deleteRule = async (id) => {
    if (!window.confirm("Bu kuralı silmek istediğinize emin misiniz?")) return;
    try {
      await api.delete(`/availability/${id}`);
      toast.success("Kural silindi");
      loadData(employeeId);
    } catch {
      toast.error("Silme başarısız");
    }
  };

  const addException = async (e) => {
    e.preventDefault();
    if (!newException.date) return toast.error("Tarih seçin");
    setSavingExc(true);
    try {
      await api.post("/availability/exceptions", {
        employeeId: Number(employeeId),
        date: newException.date,
        reason: newException.reason || null,
      });
      toast.success("Kapalı gün eklendi");
      setNewException({ date: "", reason: "" });
      loadData(employeeId);
    } catch {
      toast.error("Eklenemedi");
    } finally {
      setSavingExc(false);
    }
  };

  const deleteException = async (id) => {
    try {
      await api.delete(`/availability/exceptions/${id}`);
      toast.success("Kayıt silindi");
      loadData(employeeId);
    } catch {
      toast.error("Silme başarısız");
    }
  };

  const rulesByDay = [...rules].sort((a, b) => a.dayOfWeek - b.dayOfWeek || a.startTime.localeCompare(b.startTime));

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h2>Uygunluk & Takvim</h2>
          <p className="page-subtitle">Personel çalışma saatlerini ve kapalı günleri yönetin</p>
        </div>
      </div>

      <div className="content-card" style={{ marginBottom: 20 }}>
        <div className="form-field" style={{ maxWidth: 320 }}>
          <label>Personel</label>
          {employees.length === 0 ? (
            <p style={{ color: "#94a3b8" }}>Önce Ekip bölümünden personel ekleyin.</p>
          ) : (
            <select className="form-select" value={employeeId} onChange={(e) => setEmployeeId(e.target.value)}>
              {employees.map((emp) => (
                <option key={emp.employeeId} value={emp.employeeId}>{emp.fullName}</option>
              ))}
            </select>
          )}
        </div>
      </div>

      {employeeId && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(320px,1fr))", gap: 20, alignItems: "start" }}>
          {/* Haftalık uygunluk */}
          <div className="content-card">
            <h3 className="card-title" style={{ display: "flex", alignItems: "center", gap: 8 }}><Clock size={18} /> Haftalık Uygunluk</h3>

            {loading ? (
              <div className="page-loading inline"><div className="loading-spinner" /></div>
            ) : rulesByDay.length === 0 ? (
              <div className="empty-state" style={{ padding: "16px 0" }}><p>Henüz kural yok</p></div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 16 }}>
                {rulesByDay.map((r) => (
                  <div key={r.availabilityRuleId} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, padding: "10px 12px", background: "rgba(148,163,184,0.08)", borderRadius: 10 }}>
                    <div>
                      <span className="fw-medium">{DAYS[r.dayOfWeek]}</span>
                      <span style={{ color: "#94a3b8", marginLeft: 8 }}>{fmtTime(r.startTime)} – {fmtTime(r.endTime)}</span>
                      <span style={{ color: "#64748b", marginLeft: 8, fontSize: 12 }}>({r.slotDurationMinutes} dk)</span>
                    </div>
                    <button className="btn-icon delete" title="Sil" onClick={() => deleteRule(r.availabilityRuleId)}><Trash2 size={16} /></button>
                  </div>
                ))}
              </div>
            )}

            {/* Kural ekle */}
            <form onSubmit={addRule} style={{ borderTop: "1px solid rgba(148,163,184,0.15)", paddingTop: 16 }}>
              <div className="form-field">
                <label>Gün</label>
                <select className="form-select" value={newRule.dayOfWeek} onChange={(e) => setNewRule({ ...newRule, dayOfWeek: e.target.value })}>
                  {DAYS.map((d, i) => (<option key={i} value={i}>{d}</option>))}
                </select>
              </div>
              <div className="form-row" style={{ display: "flex", gap: 12 }}>
                <div className="form-field" style={{ flex: 1 }}>
                  <label>Başlangıç</label>
                  <input type="time" className="form-input" value={newRule.startTime} onChange={(e) => setNewRule({ ...newRule, startTime: e.target.value })} />
                </div>
                <div className="form-field" style={{ flex: 1 }}>
                  <label>Bitiş</label>
                  <input type="time" className="form-input" value={newRule.endTime} onChange={(e) => setNewRule({ ...newRule, endTime: e.target.value })} />
                </div>
                <div className="form-field" style={{ width: 110 }}>
                  <label>Slot (dk)</label>
                  <input type="number" min={5} step={5} className="form-input" value={newRule.slotDurationMinutes} onChange={(e) => setNewRule({ ...newRule, slotDurationMinutes: e.target.value })} />
                </div>
              </div>
              <button type="submit" className="btn btn-primary" disabled={savingRule}>
                {savingRule ? <Loader2 size={16} className="spin" /> : <Plus size={16} />} Kural Ekle
              </button>
            </form>
          </div>

          {/* Kapalı günler */}
          <div className="content-card">
            <h3 className="card-title" style={{ display: "flex", alignItems: "center", gap: 8 }}><CalendarOff size={18} /> Kapalı Günler</h3>

            {exceptions.length === 0 ? (
              <div className="empty-state" style={{ padding: "16px 0" }}><p>Kapalı gün yok</p></div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 16 }}>
                {exceptions.map((ex) => (
                  <div key={ex.availabilityExceptionId} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, padding: "10px 12px", background: "rgba(239,68,68,0.08)", borderRadius: 10 }}>
                    <div>
                      <span className="fw-medium">{fmtDate(ex.date)}</span>
                      {ex.reason ? <span style={{ color: "#94a3b8", marginLeft: 8 }}>{ex.reason}</span> : null}
                    </div>
                    <button className="btn-icon delete" title="Sil" onClick={() => deleteException(ex.availabilityExceptionId)}><Trash2 size={16} /></button>
                  </div>
                ))}
              </div>
            )}

            <form onSubmit={addException} style={{ borderTop: "1px solid rgba(148,163,184,0.15)", paddingTop: 16 }}>
              <div className="form-field">
                <label>Tarih</label>
                <input type="date" className="form-input" value={newException.date} onChange={(e) => setNewException({ ...newException, date: e.target.value })} />
              </div>
              <div className="form-field">
                <label>Açıklama (opsiyonel)</label>
                <input type="text" className="form-input" placeholder="Tatil, izin..." value={newException.reason} onChange={(e) => setNewException({ ...newException, reason: e.target.value })} />
              </div>
              <button type="submit" className="btn btn-primary" disabled={savingExc}>
                {savingExc ? <Loader2 size={16} className="spin" /> : <Plus size={16} />} Kapalı Gün Ekle
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
